import type { SxProps, Theme } from '@src/shared/ui/theme';
import palette from '@src/app/themes/palette';
import type { Task } from '@src/features/tasks/types';
import { Box } from '@src/shared/ui/layout';

/**
 * A task's type icon, tinted by how urgent the task has become.
 *
 * Painted as a CSS mask over `currentColor` rather than imported as a
 * component: the icon is a server-supplied URL, since which one a type carries
 * is configuration and the set is not known at build time. The tint is the
 * computed `priorities`, which is why the panel and the task list share this.
 */

const TASK_ICON_PRIORITY_COLORS = {
  1: palette.error.main,
  2: palette.warning.main,
  3: palette.success.main,
  4: palette.grey[800],
} as const;

const StyleSheet = {
  icon: {
    bgcolor: 'currentColor',
    display: 'inline-block',
    height: 24,
    maskPosition: 'center',
    maskRepeat: 'no-repeat',
    maskSize: '20px 20px',
    width: 24,
    verticalAlign: 'middle',
  },
} as const;

type TaskTypeIconProps = {
  task: Pick<Task, 'taskType' | 'priorities'>;
  sx?: SxProps<Theme>;
};

export default function TaskTypeIcon({ task, sx }: TaskTypeIconProps) {
  const icon = task.taskType.icon;
  if (!icon?.file) {
    return null;
  }

  return (
    <Box
      aria-label={icon.name}
      role="img"
      sx={[
        StyleSheet.icon,
        {
          color: TASK_ICON_PRIORITY_COLORS[task.priorities],
          maskImage: `url("${icon.file}")`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}
